import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { 
  CreditCard, 
  Plus, 
  Settings, 
  Activity, 
  BarChart3,
  IndianRupee,
  TrendingUp,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle
} from 'lucide-react';
import { ResponsiveTable, StatusBadge } from '../../components/ui/table';
import { Button } from '../../components/ui/Button/Button';
import { Input } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';
import { Filter } from '../../components/ui/Filter';
import { Pagination } from '../../components/ui/Pagination';
import { 
  addPaymentMethod, 
  updatePaymentMethod, 
  togglePaymentMethod,
  PAYMENT_STATUS,
  TRANSACTION_TYPE 
} from '../../store/paymentSlice';

const PAGE_SIZE = 15;

export function PaymentMethodsPage() {
  const dispatch = useDispatch();
  const paymentMethods = useSelector(state => state.payments.paymentMethods);
  const transactions = useSelector(state => state.payments.transactions);
  const statistics = useSelector(state => state.payments.statistics);
  
  const [activeTab, setActiveTab] = useState('methods');
  const [filter, setFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [page, setPage] = useState(1);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString();
  };

  const formatDateTime = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  const getStatusVariant = (status) => {
    switch (status) {
      case PAYMENT_STATUS.COMPLETED: return 'success';
      case PAYMENT_STATUS.PENDING: return 'warning';
      case PAYMENT_STATUS.PROCESSING: return 'info';
      case PAYMENT_STATUS.FAILED: return 'danger';
      case PAYMENT_STATUS.REFUNDED: return 'secondary';
      case PAYMENT_STATUS.CANCELLED: return 'default';
      default: return 'default';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case PAYMENT_STATUS.COMPLETED: return CheckCircle;
      case PAYMENT_STATUS.FAILED: return XCircle;
      case PAYMENT_STATUS.PENDING: 
      case PAYMENT_STATUS.PROCESSING: return Clock;
      default: return AlertCircle;
    }
  };

  const handleToggleMethod = (method) => {
    dispatch(togglePaymentMethod({ id: method.id }));
  };

  const paymentMethodColumns = [
    {
      key: 'name',
      header: 'Payment Method',
      primary: true,
      render: (value, item) => (
        <div className="flex items-center">
          <div className="p-2 bg-gray-50 rounded-lg mr-3">
            <CreditCard className="h-5 w-5 text-gray-600" />
          </div>
          <div>
            <div className="font-medium text-gray-900">{value}</div>
            <div className="text-sm text-gray-500">{item.provider}</div>
          </div>
        </div>
      )
    },
    {
      key: 'processingFee',
      header: 'Processing Fee',
      render: (value, item) => (
        <div>
          <div className="font-medium">
            {item.processingFeeType === 'percentage' ? `${value}%` : formatCurrency(value)}
          </div>
          <div className="text-sm text-gray-500">
            {item.processingFeeType === 'percentage' ? 'of transaction' : 'fixed fee'}
          </div>
        </div>
      )
    },
    {
      key: 'statistics',
      header: 'Volume',
      render: (value) => (
        <div>
          <div className="font-medium">{formatCurrency(value.totalVolume)}</div>
          <div className="text-sm text-gray-500">{value.totalTransactions.toLocaleString()} transactions</div>
        </div>
      )
    },
    {
      key: 'statistics',
      header: 'Success Rate',
      render: (value) => (
        <div className="flex items-center">
          <div className="font-medium">{value.successRate}%</div>
          <div className={`ml-2 h-2 w-16 rounded-full ${
            value.successRate >= 98 ? 'bg-green-200' : 
            value.successRate >= 95 ? 'bg-yellow-200' : 'bg-red-200'
          }`}>
            <div 
              className={`h-2 rounded-full ${
                value.successRate >= 98 ? 'bg-green-500' : 
                value.successRate >= 95 ? 'bg-yellow-500' : 'bg-red-500'
              }`}
              style={{ width: `${value.successRate}%` }}
            />
          </div>
        </div>
      )
    },
    {
      key: 'isActive',
      header: 'Status',
      render: (value) => (
        <StatusBadge 
          status={value ? 'Active' : 'Inactive'} 
          variant={value ? 'success' : 'default'} 
        />
      )
    }
  ];

  const transactionColumns = [
    {
      key: 'createdAt',
      header: 'Date',
      primary: true,
      render: (value, item) => (
        <div>
          <div className="font-medium text-gray-900">{formatDate(value)}</div>
          <div className="text-sm text-gray-500">{item.transactionId}</div>
        </div>
      )
    },
    {
      key: 'type',
      header: 'Type',
      render: (value) => (
        <span className="capitalize font-medium text-gray-700">
          {value.replace('_', ' ')}
        </span>
      )
    },
    {
      key: 'amount',
      header: 'Amount',
      render: (value, item) => (
        <div>
          <div className="font-medium">{formatCurrency(value)}</div>
          <div className="text-sm text-gray-500">
            Fee: {formatCurrency(item.processingFee)}
          </div>
        </div>
      )
    },
    {
      key: 'paymentMethod',
      header: 'Method',
      render: (value) => (
        <span className="text-sm font-medium text-gray-700">{value}</span>
      )
    },
    {
      key: 'status',
      header: 'Status',
      render: (value) => {
        const Icon = getStatusIcon(value);
        return (
          <div className="flex items-center">
            <Icon size={16} className={`mr-2 ${
              value === PAYMENT_STATUS.COMPLETED ? 'text-green-500' :
              value === PAYMENT_STATUS.FAILED ? 'text-red-500' :
              'text-yellow-500'
            }`} />
            <StatusBadge 
              status={value} 
              variant={getStatusVariant(value)} 
            />
          </div>
        );
      }
    }
  ];

  const methodActions = [
    {
      type: 'button',
      onClick: (item) => console.log('Configure:', item),
      label: 'Configure',
      variant: 'primary'
    },
    {
      type: 'button',
      onClick: handleToggleMethod,
      label: (item) => item.isActive ? 'Disable' : 'Enable',
      variant: 'secondary'
    }
  ];

  const filteredTransactions = transactions.filter(transaction => {
    const matchesSearch = transaction.transactionId.toLowerCase().includes(filter.toLowerCase()) ||
                         transaction.description.toLowerCase().includes(filter.toLowerCase()) ||
                         transaction.paymentMethod.toLowerCase().includes(filter.toLowerCase());
    const matchesStatus = statusFilter === 'all' || transaction.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const totalPages = Math.ceil(filteredTransactions.length / PAGE_SIZE);
  const start = (page - 1) * PAGE_SIZE;
  const pageItems = filteredTransactions.slice(start, start + PAGE_SIZE);

  const tabs = [
    { id: 'methods', label: 'Payment Methods', icon: CreditCard },
    { id: 'transactions', label: 'Transactions', icon: Activity },
    { id: 'analytics', label: 'Analytics', icon: BarChart3 }
  ];

  const completedTransactions = transactions.filter(t => t.status === PAYMENT_STATUS.COMPLETED).length;
  const failedTransactions = transactions.filter(t => t.status === PAYMENT_STATUS.FAILED).length;
  const pendingTransactions = transactions.filter(t => t.status === PAYMENT_STATUS.PENDING).length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Payment Methods</h1>
          <p className="mt-2 text-gray-600">
            Configure payment gateways and monitor transactions
          </p>
        </div>
        <Button variant="primary" className="mt-4 sm:mt-0">
          <Plus size={16} className="mr-2" />
          Add Payment Method
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center">
            <div className="p-2 bg-green-50 rounded-lg">
              <IndianRupee className="h-6 w-6 text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total Revenue</p>
              <p className="text-2xl font-bold text-gray-900">{formatCurrency(statistics.totalRevenue)}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center">
            <div className="p-2 bg-blue-50 rounded-lg">
              <Activity className="h-6 w-6 text-blue-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total Transactions</p>
              <p className="text-2xl font-bold text-gray-900">{statistics.totalTransactions.toLocaleString()}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center">
            <div className="p-2 bg-purple-50 rounded-lg">
              <TrendingUp className="h-6 w-6 text-purple-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Success Rate</p>
              <p className="text-2xl font-bold text-gray-900">{statistics.successRate}%</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center">
            <div className="p-2 bg-orange-50 rounded-lg">
              <Settings className="h-6 w-6 text-orange-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Processing Fees</p>
              <p className="text-2xl font-bold text-gray-900">{formatCurrency(statistics.totalProcessingFees)}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === tab.id
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <tab.icon size={16} className="mr-2" />
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'methods' && (
        <div className="space-y-6">
          <ResponsiveTable
            data={paymentMethods}
            columns={paymentMethodColumns}
            actions={methodActions}
          />
        </div>
      )}

      {activeTab === 'transactions' && (
        <div className="space-y-6">
          {/* Transaction Stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-center">
                <CheckCircle className="h-5 w-5 text-green-600 mr-2" />
                <div>
                  <p className="text-sm font-medium text-green-800">Completed</p>
                  <p className="text-lg font-bold text-green-900">{completedTransactions}</p>
                </div>
              </div>
            </div>
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-center">
                <XCircle className="h-5 w-5 text-red-600 mr-2" />
                <div>
                  <p className="text-sm font-medium text-red-800">Failed</p>
                  <p className="text-lg font-bold text-red-900">{failedTransactions}</p>
                </div>
              </div>
            </div>
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <div className="flex items-center">
                <Clock className="h-5 w-5 text-yellow-600 mr-2" />
                <div>
                  <p className="text-sm font-medium text-yellow-800">Pending</p>
                  <p className="text-lg font-bold text-yellow-900">{pendingTransactions}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Filters */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
           
              <Filter
                value={filter}
                onChange={(e) => {
                  setFilter(e.target.value);
                  setPage(1);
                }}
                placeholder="Search transactions..."
              />
          
            <Select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setPage(1);
              }}
              options={['all', ...Object.values(PAYMENT_STATUS)]}
              className="w-full sm:w-auto"
            />
          </div>

          {/* Transactions Table */}
          <ResponsiveTable
            data={pageItems}
            columns={transactionColumns}
          />

          {totalPages > 1 && (
            <Pagination
              currentPage={page}
              totalPages={totalPages}
              onPageChange={setPage}
            />
          )}
        </div>
      )}

      {activeTab === 'analytics' && (
        <div className="space-y-6">
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Payment Analytics</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h3 className="font-medium text-gray-900 mb-3">Payment Method Distribution</h3>
                <div className="space-y-3">
                  {paymentMethods.map((method) => (
                    <div key={method.id} className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">{method.name}</span>
                      <div className="flex items-center">
                        <div className="w-24 bg-gray-200 rounded-full h-2 mr-3">
                          <div 
                            className="bg-blue-500 h-2 rounded-full" 
                            style={{ 
                              width: `${(method.statistics.totalTransactions / statistics.totalTransactions) * 100}%` 
                            }}
                          />
                        </div>
                        <span className="text-sm font-medium text-gray-900">
                          {((method.statistics.totalTransactions / statistics.totalTransactions) * 100).toFixed(1)}%
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <h3 className="font-medium text-gray-900 mb-3">Revenue by Method</h3>
                <div className="space-y-3">
                  {paymentMethods.map((method) => (
                    <div key={method.id} className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">{method.name}</span>
                      <span className="text-sm font-medium text-gray-900">
                        {formatCurrency(method.statistics.totalVolume)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
